import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { removeNotification } from '../../state/notificationsState'

import { useAppDispatch } from '@/core/store/hooks'

interface NotificationUiResult {
  onClose: () => void
  isExiting: boolean
  isExitingRef: RefObject<boolean>
  handleExitComplete: () => void
}

export const useNotificationUi = (
  id: number,
  duration: number,
  reiniciadoEn?: number,
): NotificationUiResult => {
  const dispatch = useAppDispatch()
  const [isExiting, setIsExiting] = useState(false)
  const isExitingRef = useRef(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const hasClosedRef = useRef(false)

  const onClose = useCallback((): void => {
    if (hasClosedRef.current) {return}
    hasClosedRef.current = true
    dispatch(removeNotification({ value: id }))
  }, [dispatch, id])

  // Callback para cuando termine la animación de salida
  const handleExitComplete = useCallback((): void => {
    if (isExitingRef.current) {
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    // Resetear refs cuando el componente se monta con un nuevo id, y también cuando el
    // mismo aviso se repite (reiniciadoEn cambia): así el toast no desaparece mientras el
    // problema sigue ocurriendo, sin necesidad de volver a montar el componente.
    hasClosedRef.current = false
    isExitingRef.current = false
    setIsExiting(false)

    // Timer para cierre automático después del timeout
    timerRef.current = setTimeout(() => {
      if (!isExitingRef.current && !hasClosedRef.current) {
        setIsExiting(true)
        isExitingRef.current = true
      }
    }, duration)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [id, duration, reiniciadoEn])

  return {
    onClose,
    isExiting,
    isExitingRef,
    handleExitComplete,
  }
}
